import api from './api';
import {
    CHECKLIST_PENDING_STEP_STORAGE_KEY,
    getApiStatusCode,
    getChecklistContext,
    isChecklistSaveSuccess,
    markChecklistStorageWrite,
    notifyChecklistProgressUpdated,
    parseGuidOrNull,
    resolveCompanyId
} from '@/helpers/checklistCompletion';

const toLower = (value) => String(value || '').trim().toLowerCase();

const TERMINAL_STEP_STATUSES = ['completed', 'complete', 'done', '1', 'true', 'skipped', 'skiped'];

export const isVendorChecklistStepName = (stepName) => {
    const name = toLower(stepName);
    return name.includes('add vendor') || name.includes('vendor checklist');
};

const clearChecklistContext = () => {
    try {
        localStorage.removeItem(CHECKLIST_PENDING_STEP_STORAGE_KEY);
    } catch (e) {
        // no-op
    }
};

const persistChecklistVisibilityState = async (companyId) => {
    if (!companyId || !parseGuidOrNull(companyId)) {
        return;
    }
    try {
        const response = await api.get(`Onboarding/${companyId}/checkList`);
        const payload = response?.data;
        const items = Array.isArray(payload) ? payload : (Array.isArray(payload?.result) ? payload.result : []);
        const pendingCount = (items || []).filter((step) => {
            const status = toLower(step?.stepStatus);
            return !TERMINAL_STEP_STATUSES.includes(status);
        }).length;
        const stateKey = `onboarding_checklist_state_${companyId}`;
        const stateValue = {
            pendingCount,
            totalSteps: (items || []).length,
            doneCount: (items || []).length - pendingCount,
            allDone: pendingCount === 0 && (items || []).length > 0,
            updatedAt: new Date().toISOString()
        };
        markChecklistStorageWrite();
        localStorage.setItem(stateKey, JSON.stringify(stateValue));
        localStorage.setItem('IsOnboardingCompleted', stateValue.allDone ? 'true' : 'false');
        localStorage.setItem('isCheckListOnboardingstep', stateValue.allDone ? 'true' : 'false');
    } catch (e) {
        // no-op
    }
};

const buildUpdateStepStatusModel = (stepId, companyId) => {
    const stepIdNumber = Number(stepId);
    const companyGuid = parseGuidOrNull(companyId) || resolveCompanyId();
    if (!Number.isFinite(stepIdNumber) || stepIdNumber < 1 || !companyGuid) {
        return null;
    }
    return {
        stepId: stepIdNumber,
        companyId: companyGuid,
        stepStatus: 'Completed',
        modifiedBy: localStorage.getItem('userId') || null
    };
};

class OnboardingService {
    async getOnboardingSteps(companyId, steps) {
        if (steps == null || steps == '') {
            steps = 'checkList';
        }
        return await api.get(`Onboarding/${companyId}/${steps}`);
    }

    async updateOnboardingStepStatus(model) {
        return await api.post('Onboarding/UpdateStepStatus', model);
    }

    /**
     * POST Onboarding/UpdateStepStatus — same contract as onboarding wizard.
     */
    async completePendingChecklistStep() {
        const context = getChecklistContext();
        if (!context?.stepId || toLower(context.source) !== 'checklist') {
            return false;
        }

        const companyId = parseGuidOrNull(context.companyId) || resolveCompanyId();
        if (!companyId) {
            return false;
        }

        const model = buildUpdateStepStatusModel(context.stepId, companyId);
        if (!model) {
            return false;
        }

        try {
            let response = await this.updateOnboardingStepStatus(model);

            if (!isChecklistSaveSuccess(response) && model.modifiedBy) {
                response = await this.updateOnboardingStepStatus({
                    ...model,
                    modifiedBy: null
                });
            }

            if (!isChecklistSaveSuccess(response)) {
                const statusCode = getApiStatusCode(response?.data);
                if (statusCode === 404) {
                    return false;
                }
                return false;
            }

            clearChecklistContext();
            await persistChecklistVisibilityState(companyId);
            notifyChecklistProgressUpdated(companyId);
            return true;
        } catch (e) {
            return false;
        }
    }

    async saveCompanyType(model) {
        return await api.post('CompanyUsers/UpdateCompanyType', model);
    }

    async getCompanyType(companyId, userId) {
        return await api.get(`CompanyUsers/GetCompanyType/${companyId}/${userId}`);
    }

    async getCompanyAssetDropdown(companyId, userId, userType) {
        return await api.get(`Blog/GetCompanyassetDrp/${companyId}/${userId}/${userType}`);
    }

    async companyHasValidVendors(companyId) {
        const companyGuid = parseGuidOrNull(companyId) || resolveCompanyId();
        if (!companyGuid) {
            return false;
        }
        try {
            const UserService = (await import('@/service/user.service.js')).default;
            const searchModel = {
                searchItem: '00Test',
                status: -1,
                sortColumn: 'CreatedOn',
                sortDir: 'asc',
                pageIndex: 0,
                pageSize: 1,
                userId: localStorage.getItem('userId') || localStorage.getItem('switcheduserId') || '',
                companyId: companyGuid
            };
            const response = await UserService.GetVendorList(searchModel);
            const totalItems = Number(response?.data?.pager?.totalItems ?? 0);
            return totalItems > 0;
        } catch (e) {
            return false;
        }
    }

    /**
     * Mark the vendor checklist step completed when the company already has vendors.
     * Uses the same vendor list API and company context as Manage Vendors.
     */
    async syncVendorChecklistStep(companyId, options = {}) {
        const notify = options.notify !== false;
        const companyGuid = parseGuidOrNull(companyId) || resolveCompanyId();
        if (!companyGuid) {
            return { updated: false, stepId: null };
        }

        let vendorStep = null;
        const providedSteps = Array.isArray(options.steps) ? options.steps : null;
        try {
            if (providedSteps) {
                vendorStep = providedSteps.find((step) => isVendorChecklistStepName(step?.stepName || step?.stepHeading));
            } else {
                const stepsResponse = await this.getOnboardingSteps(companyGuid, 'checkList');
                const payload = stepsResponse?.data;
                const items = Array.isArray(payload) ? payload : (Array.isArray(payload?.result) ? payload.result : []);
                vendorStep = (items || []).find((step) => isVendorChecklistStepName(step?.stepName || step?.stepHeading));
            }
        } catch (e) {
            return { updated: false, stepId: null };
        }

        if (!vendorStep) {
            return { updated: false, stepId: null };
        }

        const stepId = vendorStep.id ?? vendorStep.Id;
        const status = toLower(vendorStep.stepStatus);
        if (TERMINAL_STEP_STATUSES.includes(status)) {
            return { updated: false, stepId };
        }

        const hasVendors = await this.companyHasValidVendors(companyGuid);
        if (!hasVendors) {
            return { updated: false, stepId };
        }

        const model = buildUpdateStepStatusModel(stepId, companyGuid);
        if (!model) {
            return { updated: false, stepId };
        }

        try {
            let response = await this.updateOnboardingStepStatus(model);

            if (!isChecklistSaveSuccess(response) && model.modifiedBy) {
                response = await this.updateOnboardingStepStatus({
                    ...model,
                    modifiedBy: null
                });
            }

            if (!isChecklistSaveSuccess(response)) {
                return { updated: false, stepId };
            }

            await persistChecklistVisibilityState(companyGuid);
            if (notify) {
                notifyChecklistProgressUpdated(companyGuid);
            }
            return { updated: true, stepId };
        } catch (e) {
            return { updated: false, stepId };
        }
    }
}

export default new OnboardingService();
